import * as React from "react"
import { useTranslation } from "react-i18next"

import {
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"
import { useAppConfig } from "@/providers/config-provider"
import { useLocation, useNavigate } from "react-router-dom"
import { usePermissions } from "@/providers/permission-provider"
import { useQueryParams } from "@/hooks/use-query-params"
import { FRONTEND_ROUTES } from "@/constants"
import { cn } from "@/lib/utils"
import { PermissionBroker } from "@/definitions/types/permissions/permissions"
import { BrokerSelector } from "@/components/common/BrokerSelector"
import { PermissionsRoles } from "@/definitions/enums/permissions"

const PLACEHOLDER = "placeholder"
const BROKER_ID_QUERY_PARAM = "brokerId"

// Business logic functions
const createBrokersList = (
  brokers: PermissionBroker[] | undefined,
  isAdmin: boolean,
  placeholder: PermissionBroker
) => {
  return isAdmin
    ? [placeholder, ...(brokers || [])]
    : brokers || []
}

const getInitialBrokerSelection = (
  brokers: PermissionBroker[] | undefined,
  isAdmin: boolean,
  brokerId: string | null | undefined,
  placeholder: PermissionBroker
): PermissionBroker => {
  // Rule 1: If regular user has only one broker, auto-select it
  if (brokers?.length === 1 && !isAdmin) {
    return brokers[0]
  }

  // Rule 2: If no brokerId provided, use placeholder
  if (!brokerId) {
    return placeholder
  }
  // Rule 3: Find broker by ID, fallback to placeholder
  const availableBrokers = createBrokersList(
    brokers,
    isAdmin,
    placeholder
  )
  const foundBroker = availableBrokers.find(
    (broker) => broker.id === brokerId
  )
  return foundBroker || placeholder
}

export const PortalSidebarHeader = () => {
  const { t } = useTranslation()
  const {
    toggleSidebar,
    isMobile,
    open: sidebarOpen,
    setOpen: setSidebarOpen,
  } = useSidebar()

  const { rolesAndBrokersData, isSuperAdmin } =
    usePermissions()
  const [open, setOpen] = React.useState(false)
  const config = useAppConfig()
  const location = useLocation()
  const pathname = location.pathname
  const navigate = useNavigate()
  const queryParams = useQueryParams()

  const sidebarClosedLogo =
    config?.visualPreferences.layout.images.favicon ??
    "/p2b_512.png"

  const hasAdminRole =
    rolesAndBrokersData?.roles.includes(
      PermissionsRoles.ADMIN
    ) || false

  const brokerId = queryParams.brokerId

  const placeholder: PermissionBroker = {
    id: PLACEHOLDER,
    name: t("sidebar.allBrokers"),
  }

  const brokers = createBrokersList(
    rolesAndBrokersData?.brokers,
    isSuperAdmin || hasAdminRole,
    placeholder
  )

  const [selectedBroker, setSelectedBroker] =
    React.useState<PermissionBroker>(() =>
      getInitialBrokerSelection(
        rolesAndBrokersData?.brokers,
        isSuperAdmin || hasAdminRole,
        brokerId,
        placeholder
      )
    )

  const handleBrokerSelect = (broker: PermissionBroker) => {
    setSelectedBroker(broker)
    setOpen(false)

    const searchParams = new URLSearchParams(
      window.location.search
    )

    if (broker.id === PLACEHOLDER) {
      searchParams.delete(BROKER_ID_QUERY_PARAM)
    } else {
      searchParams.set(BROKER_ID_QUERY_PARAM, broker.id)
    }

    navigate(`${pathname}?${searchParams.toString()}`)
  }

  const handleLogoClick = () => {
    navigate(
      `${FRONTEND_ROUTES.MAIN.route}${brokerId ? `?${BROKER_ID_QUERY_PARAM}=${brokerId}` : ""}`
    )
    if (isMobile) {
      toggleSidebar()
    }
  }

  const handleOpenSidebar = () => {
    if (!sidebarOpen) {
      setSidebarOpen(true)
      setTimeout(() => {
        setOpen(true)
      }, 150)
    } else {
      setOpen(!open)
    }
  }

  return (
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size="lg"
            className="justify-center hover:bg-gray-200 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            onClick={handleLogoClick}
          >
            <img
              src={sidebarClosedLogo}
              alt="Merchant Shield"
              width={20}
              height={20}
              className={cn(
                sidebarOpen ? "hidden" : "flex"
              )}
            />
            <ImageWithFallback
              src={config?.merchant.logo}
              alt="Merchant Logo"
              width={80}
              height={70}
              className={cn(
                "h-10 place-self-center object-contain",
                sidebarOpen ? "opacity-100" : "hidden"
              )}
            />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
      <SidebarMenu>
        <SidebarMenuItem>
          <BrokerSelector
            brokers={brokers}
            selectedBroker={selectedBroker}
            onBrokerSelect={handleBrokerSelect}
            onSidebarOpen={handleOpenSidebar}
          />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  )
}
